import React, { useCallback, useMemo, useRef } from "react";
import classNames from "classnames/bind";
import styles from "./NotificationFilters.module.scss";
import { FilterButtons } from "../../../../components/FilterButtons";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "../../../../hooks/useSearchParams";
import { useTranslation } from "react-i18next";
import { useGetNotificationTypesQuery } from "../../../../providers/notifications";

const cx = classNames.bind(styles);

interface NotificationFiltersProps {
  onFilterChange?: (type: string | null) => void;
  unreadCount?: number;
  isAuthenticated?: boolean;
  customerId?: string;
}

const NotificationFilters = ({
  onFilterChange,
  unreadCount = 0,
  isAuthenticated = false,
  customerId,
}: NotificationFiltersProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { searchParams, set, delete: deleteParam } = useSearchParams();
  const selectedType = searchParams.get("notificationType");

  const { data: availableTypes = [], isLoading: typesLoading } = useGetNotificationTypesQuery({
    params: {
      customerId,
      isPublicOnly: !isAuthenticated,
    },
  });

  const notificationTypes = useMemo(() => {
    if (typesLoading) {
      return [{ key: "all", label: t("All"), showCount: false }];
    }

    const types = [{ key: "all", label: t("All"), showCount: false }];

    if (isAuthenticated) {
      types.push({ key: "unread", label: t("Unread"), showCount: true });
    }

    availableTypes
      .filter((item) => item.type && item.type !== "all")
      .forEach((item) => {
        const label =
          item.typeName && item.typeName.trim()
            ? item.typeName
            : item.type.charAt(0).toUpperCase() + item.type.slice(1);

        types.push({
          key: item.type,
          label: label,
          showCount: false,
        });
      });

    return types;
  }, [t, isAuthenticated, availableTypes, typesLoading]);

  const handleFilterClick = useCallback(
    async (type: string) => {
      console.log("handleFilterClick", type);

      if (notificationTypes.length > 3) {
        scrollContainerRef.current?.scrollTo({
          left: 0,
          behavior: "smooth",
        });
      }

      if (type === "all" || type === selectedType) {
        deleteParam("notificationType");
        onFilterChange?.(null);
      } else {
        set("notificationType", type);
        onFilterChange?.(type);
      }

      await queryClient.invalidateQueries({
        queryKey: ["notifications"],
      });
    },
    [selectedType, deleteParam, set, onFilterChange, queryClient, notificationTypes.length],
  );

  return (
    <div className="w-full flex gap-2 py-1">
      <div
        ref={scrollContainerRef}
        className={cx(
          "flex-1 flex overflow-x-auto overflow-y-visible gap-2 scroll-smooth webkit-overflow-scrolling-touch flex-nowrap min-w-0 pt-2.5 -mt-2.5",
          "hiddenScrollbar",
        )}
      >
        {notificationTypes.map(({ key, label, showCount }, index) => {
          const isActive = key === "all" ? !selectedType : selectedType === key;
          return (
            <div key={index} className="relative flex-shrink-0 min-w-fit whitespace-nowrap">
              <FilterButtons filterName={label} handleFilterClick={() => handleFilterClick(key)} isActive={isActive} />
              {showCount && key === "unread" && unreadCount > 0 && (
                <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full font-medium min-w-[1.125rem] h-[1.125rem] flex items-center justify-center px-1">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

NotificationFilters.displayName = "Parhato2_NotificationFilters";

export { NotificationFilters };

export default NotificationFilters;
